import React, { useEffect, useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { StackedDialog } from '../utilities/ModalStackContext';

// Email a service estimate or invoice to the customer with no signature
// request. Use this when the customer only needs to see the document — an
// approval quote, a copy for their records, or an invoice to pay.
export default function SendServiceDocumentDialog({ open, onClose, docLabel, stock, defaultTo, onSend }) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Reset to the order's current customer and document type each time it opens.
  useEffect(() => {
    if (!open) return;
    setTo(defaultTo || '');
    setCc('');
    setSubject(`Skyway Classics ${docLabel}${stock ? ` - Service Order ${stock}` : ''}`);
    setMessage(`Your ${docLabel.toLowerCase()} from Skyway Classics is attached.`);
    setSending(false);
  }, [open, defaultTo, docLabel, stock]);

  const splitEmails = (value) => value.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);

  const handleSend = async () => {
    setSending(true);
    const sent = await onSend({
      to: splitEmails(to),
      cc: splitEmails(cc),
      subject: subject.trim(),
      message: message.trim(),
    });
    setSending(false);
    if (sent) onClose();
  };

  return (
    <StackedDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Email {docLabel} to Customer</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Sends the {docLabel.toLowerCase()} as a PDF attachment. No signature is requested.
        </DialogContentText>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="customer@example.com"
            helperText="Separate multiple addresses with commas."
            fullWidth
            size="small"
          />
          <TextField
            label="CC (optional)"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !splitEmails(to).length || !subject.trim()}
          startIcon={sending ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {sending ? 'Sending...' : `Send ${docLabel}`}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
